import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import FolderGeneralInfoCard from './FolderGeneralInfoCard';
import {
  GET_ACCOUNT_MANAGERS_QUERY,
  GET_RISK_RESPONSIBLES_QUERY,
  GET_PARTNER_QUERY,
  GET_PARTNERS_LIST_QUERY,
  GET_FOLDER_HEADER_QUERY
} from '../queries';

import { OFFER_TYPE_QUERY } from '../../../../../offer/edit/financials/additionalData/queries';

import { UPDATE_FOLDER_GENERAL_INFO_MUTATION, SAVE_NEW_FOLDER_MUTATION, CALCULATE_APPROVAL_MUTATION } from '../mutations';

import { FOLDER_GENERAL_INFO_UPDATED, NEW_FOLDER_SAVED } from '../subscriptions';

import { shape, required } from '@totalsoft/pure-validations';
import { useValidation } from '@totalsoft/pure-validations-react';
import withCustomComponent from 'providers/withCustomComponent';
import offerType from 'constants/offerType';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { useDictionary } from '../../../../../offer/edit/financials/additionalData/hooks/useDictionary';
import { useMutation } from '@apollo/client';
import { showError } from 'actions/actionCreators/snackbarActions';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import useDebounce from 'utils/hooks/useDebounce';
import { getInt } from 'utils/functions';

const validatorNewFolderHeader = shape({
  clientId: required
});

const FolderGeneralInfoContainer = ({ documentId, siteId, documentTypeId, isFacilityApproval, history }) => {
  const [isDirty, setIsDirty] = useState(false);
  const [validationNewFolderHeader, validateNewFolderHeader] = useValidation(validatorNewFolderHeader);
  const localGeneralInfo = {
    generalInfo: {
      client: {},
      partnerGroupId: null,
      isFacilityApproval,
      partnerGroup: '',
      folderTypeId: isFacilityApproval ? offerType.facilityOffer.id : offerType.newRequest.id
    }
  };
  const [state, setState] = useState(localGeneralInfo);
  const [saving, setSaving] = useState(false);

  const [responsibleOptions, setResponsibleOptions] = useState([]);
  const [partnersOptions, setPartnersOptions] = useState([]);
  const [accountManagersOptions, setAccountManagersOptions] = useState([]);

  useQueryWithErrorHandling(GET_RISK_RESPONSIBLES_QUERY, {
    onCompleted: data => {
      const responsibles = data?.riskResponsibles;
      if (responsibles)
        setResponsibleOptions(
          responsibles.map(item => {
            return { responsibleId: item.responsibleId, responsibleName: item.name };
          })
        );
    }
  });
  const responsiblesOptionsDebounced = useDebounce(responsibleOptions, 500);

  useQueryWithErrorHandling(GET_PARTNERS_LIST_QUERY, {
    variables: { search: '' },
    skip: documentId,
    onCompleted: data => {
      const partnersList = data?.partnersList;
      if (partnersList)
        setPartnersOptions(
          partnersList.map(item => {
            return { clientId: item.id, uniqueId: item.uniqueId, clientName: item.partnerName };
          })
        );
    }
  });
  const partnersOptionsDebounced = useDebounce(partnersOptions, 500);

  useQueryWithErrorHandling(GET_ACCOUNT_MANAGERS_QUERY, {
    variables: { search: '' },
    onCompleted: data => {
      const accountManagers = data?.accountManagers;
      if (accountManagers)
        setAccountManagersOptions(
          accountManagers.map(item => {
            return { accountManagerId: item.accountManagerId, accountManagerName: item.name };
          })
        );
    }
  });
  const accountManagersOptionsDebounced = useDebounce(accountManagersOptions, 500);

  const folderTypes = useDictionary(OFFER_TYPE_QUERY, {
    variables: { documentTypeId },
    skip: !documentTypeId
  });

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
    }
  });

  useQueryWithErrorHandling(GET_FOLDER_HEADER_QUERY, {
    variables: { documentId: getInt(documentId), siteId: getInt(siteId) },
    skip: !documentId,
    onCompleted: data => {
      setState(previousState => ({
        ...previousState,
        generalInfo: {
          ...data.folderHeader.generalInfo,
          client: data.folderHeader.client,
          partnerGroup: data.folderHeader.partnerGroup,
          partnerGroupId: data.folderHeader.partnerGroupId
        }
      }));
    }
  });

  useQueryWithErrorHandling(GET_PARTNER_QUERY, {
    variables: { uniqueId: state?.generalInfo?.client?.uniqueId },
    skip: !state.generalInfo.client?.uniqueId || documentId,
    onCompleted: data => {
      onGeneralInfoChange({ clientName: data.partner.partnerName }, 'client');
    }
  });

  const [saveNewFolder] = useMutation(SAVE_NEW_FOLDER_MUTATION, {
    onError: error => {
      showError(error.message);
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.saveNewFolder;
      storeKey({ correlationId });
    }
  });

  const [updateFolderGeneralInfo] = useMutation(UPDATE_FOLDER_GENERAL_INFO_MUTATION, {
    onError: error => {
      showError(error.message);
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.updateFolderGeneralInfo;
      storeKey({ correlationId });
    }
  });

  const [calculateApproval] = useMutation(CALCULATE_APPROVAL_MUTATION, {
    onError: error => {
      showError(error.message);
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.calculateApproval;
      storeKey({ correlationId });
    }
  });

  useEffect(() => {
    validateNewFolderHeader(state.generalInfo.client);
  }, [state.generalInfo.client]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRedirect = (documentId, siteId) => {
    history.push(`/folders/${documentId}/${siteId}`);
  };

  const onCalculateApproval = () => {
    calculateApproval({
      variables: {
        input: {
          documentId,
          siteId
        }
      }
    });
  };

  const onSaveNewCallBack = (documentId, siteId) => {
    if (!documentId) return;
    onRedirect(documentId, siteId);
  };

  const onSave = () => {
    if (!validateNewFolderHeader(state.generalInfo.client)) return;
    setSaving(true);

    if (!documentId) {
      const dataNewFolder = {
        responsibleId: state.generalInfo.responsibleId,
        responsibleName: state.generalInfo.responsibleName,
        accountManagerId: state.generalInfo.accountManagerId,
        accountManagerName: state.generalInfo.accountManagerName,
        folderTypeId: state.generalInfo.folderTypeId,
        folderType: state.generalInfo.folderType,
        clientId: state.generalInfo.client.clientId,
        clientName: state.generalInfo.client.clientName,
        uniqueId: state.generalInfo.client.uniqueId,
        riskAnalysisTypeId: state.generalInfo.riskAnalysisTypeId,
        isFacilityApproval: isFacilityApproval
      };
      saveNewFolder({
        variables: {
          input: dataNewFolder
        }
      });
    } else {
      const dataForUpdate = {
        documentId: documentId,
        siteId: siteId,
        responsibleId: state.generalInfo.responsibleId,
        responsibleName: state.generalInfo.responsibleName,
        accountManagerId: state.generalInfo.accountManagerId,
        accountManagerName: state.generalInfo.accountManagerName,
        folderTypeId: state.generalInfo.folderTypeId,
        folderType: state.generalInfo.folderType,
        riskAnalysisTypeId: state.generalInfo.riskAnalysisTypeId
      };
      updateFolderGeneralInfo({
        variables: {
          input: dataForUpdate
        }
      });
    }
  };

  const onGeneralInfoChange = (value, prop = null) => {
    setState(generalInfoUpdater(value, prop));
    setIsDirty(true);
  };

  const generalInfoUpdater =
    (value, prop = null) =>
    prevState => {
      if (prop === 'client')
        return {
          generalInfo: { ...prevState.generalInfo, client: { ...prevState.generalInfo.client, ...value } }
        };
      return {
        generalInfo: prop ? { ...prevState.generalInfo, [prop]: value } : { ...prevState.generalInfo, ...value }
      };
    };

  const openLinkedDocument = (documentId, documentSiteId) => e => {
    e.stopPropagation();
    if (isFacilityApproval) window.open(`/facilities/${documentId}/${documentSiteId}/generalData`, '_blank');
  };

  useGlobalSubscription(NEW_FOLDER_SAVED, {
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setSaving(false);
      setIsDirty(false);
      onSaveNewCallBack(data.newFolderSaved.documentId, data.newFolderSaved.siteId);
    }
  });

  const isNewDocument = useMemo(() => getInt(documentId) === null || getInt(siteId) === null, [documentId, siteId]);

  useGlobalSubscription(FOLDER_GENERAL_INFO_UPDATED, {
    variables: {
      documentId,
      siteId
    },
    skip: isNewDocument,
    onSubscriptionData: ({ client }) => {
      setSaving(false);
      setIsDirty(false);
      client.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: `folderHeader({"documentId":${documentId},"siteId":${siteId}})`
      });
      client.cache.gc();
    }
  });

  return (
    <FolderGeneralInfoCard
      generalInfo={state.generalInfo}
      savingGeneralInfo={saving}
      onSave={onSave}
      isDirty={isDirty}
      validation={validationNewFolderHeader}
      isNewFolder={!documentId}
      onGeneralInfoChange={onGeneralInfoChange}
      accountManagersOptions={accountManagersOptionsDebounced}
      responsiblesOptions={responsiblesOptionsDebounced}
      partnersOptions={partnersOptionsDebounced}
      folderTypes={folderTypes.data}
      openLinkedDocument={openLinkedDocument}
      onCalculateApproval={onCalculateApproval}
      isFacilityApproval={isFacilityApproval}
    />
  );
};

FolderGeneralInfoContainer.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  documentTypeId: PropTypes.number,
  isFacilityApproval: PropTypes.bool,
  history: PropTypes.object.isRequired
};

export default withCustomComponent('FolderGeneralInfoContainer')(FolderGeneralInfoContainer);
